import { useState, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText, Link2, Upload, BookOpen, ClipboardList, BarChart2,
  User, Loader2, Check, AlertTriangle, RotateCcw, ChevronRight,
  MessageSquare, Sparkles, Download,
} from "lucide-react";
import { invokeFunction } from "@/integrations/supabase/client";
import type { Taxonomy, LearnerProfile, Completion } from "@/lib/learnerHub/types";
import { ROLE_PROFILES } from "@/lib/learnerHub/constants";

// ─── Types ────────────────────────────────────────────────────────────────────

type ConfLevel = "HIGH" | "MEDIUM" | "LOW" | "UNKNOWN";
type EvidenceSrc = "resume" | "linkedin" | "pretest" | "scenario";

interface FlatUnit {
  id: string;
  label: string;
  type: "application" | "knowledge";
  ind?: string;
}

interface Evidence {
  id: string;
  unitId: string;
  confidence: ConfLevel;
  evidence: string;
  evidenceDate: string | null;
  source: EvidenceSrc;
  sourceTier: number;
}

interface DocResult {
  unitId: string;
  confidence: ConfLevel;
  evidence: string;
  evidenceDate: string | null;
  reasoning: string;
}

interface PreTestQ {
  unitId: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

interface ScenarioData {
  scenario: string;
  prompt: string;
  keyElements: string[];
}

interface ScenarioEval {
  confidence: ConfLevel;
  score: number;
  strengths: string;
  gaps: string;
  verdict: string;
}

interface EffBest {
  unitId: string; confidence: ConfLevel; evidence: string;
  evidenceDate: string | null; source: EvidenceSrc; sourceTier: number;
  decayedConfidence: ConfLevel; _score: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SOURCE_TIER: Record<EvidenceSrc, number> = { resume: 1, linkedin: 2, pretest: 3, scenario: 4 };
const SOURCE_LABEL: Record<EvidenceSrc, string> = { resume: "Resume", linkedin: "LinkedIn", pretest: "Pre-test", scenario: "Scenario" };
const DECAY_HL: Record<EvidenceSrc, number> = { resume: 2, linkedin: 2, pretest: 3, scenario: 2 };
const CONF_RANK: Record<ConfLevel, number> = { HIGH: 3, MEDIUM: 2, LOW: 1, UNKNOWN: 0 };
const CONF_LABELS: ConfLevel[] = ["UNKNOWN", "LOW", "MEDIUM", "HIGH"];

const SAMPLE_RESUME = `RESUMEN PROFESIONAL
Operador de equipos mineros con 6 años de experiencia en minería a rajo abierto. Especializado en operación de camión minero de gran tonelaje y equipos auxiliares. Certificado bajo el Marco de Cualificaciones CCM-Eleva. Cero accidentes con tiempo perdido desde 2021.

EXPERIENCIA

Operador de Camión Minero — Mina Collahuasi (Compañía Minera del Norte) — Mar 2022–Presente
- Operación diaria de camión Komatsu 930E (290 ton) en circuito de acarreo a chancador primario
- Aplicación rigurosa de normativa DS-132 y procedimientos internos HSEC en cada turno
- Ejecución de pre-operacionales completos, bloqueo y etiquetado (LOTO) antes de intervenciones
- Operación de camión aljibe para control de polvo en caminos de acarreo en temporada seca
- Mentor de dos operadores nuevos en inducción a terreno durante 2024

Operador de Equipos Auxiliares — Esperanza Mine — Ene 2021–Feb 2022
- Operación de motoniveladora (tractor neumático) para mantenimiento y perfilado de caminos mina
- Apoyo a operaciones de excavadora hidráulica Komatsu PC3000 en frente de carguío
- Cumplimiento de estándares de seguridad del sitio y turnos rotativos 7×7

CERTIFICACIONES Y EDUCACIÓN
Licencia de Conducir Clase A — Registro Civil, 2020
Certificación CCM-Eleva UCL-001: Trabajar con seguridad según DS-132 — 2022
Certificación CCM-Eleva UCL-002: Operar camión minero de gran tonelaje — 2022
Instituto Técnico Minero OTEC — Módulo Equipos Pesados (camión, excavadora, motoniveladora) — 2020`;

// ─── Utility functions ────────────────────────────────────────────────────────

function flattenUnits(taxonomy: Taxonomy): FlatUnit[] {
  return taxonomy.processes.flatMap((p) =>
    p.qualifications.flatMap((q) =>
      q.units.map((u) => ({
        id: u.id,
        label: u.label,
        type: (u.type ?? "knowledge") as "application" | "knowledge",
        ind: u.ind,
      }))
    )
  );
}

function decayConf(confidence: ConfLevel, source: EvidenceSrc, dateStr: string | null): ConfLevel {
  if (!dateStr) return confidence;
  const age = Math.max(0, (Date.now() - new Date(dateStr).getTime()) / (365.25 * 86400000));
  const rank = Math.max(0, (CONF_RANK[confidence] ?? 0) - Math.floor(age / (DECAY_HL[source] ?? 2)));
  return CONF_LABELS[rank];
}

function effectiveCoverage(assessments: Evidence[]): Record<string, EffBest> {
  const best: Record<string, EffBest> = {};
  for (const a of assessments) {
    const dc = decayConf(a.confidence, a.source, a.evidenceDate);
    const score = a.sourceTier * 10 + (CONF_RANK[dc] ?? 0);
    if (!best[a.unitId] || score > best[a.unitId]._score) {
      best[a.unitId] = { ...a, decayedConfidence: dc, _score: score };
    }
  }
  return best;
}

function detectConflicts(assessments: Evidence[]): Record<string, { type: string; msg: string }> {
  const out: Record<string, { type: string; msg: string }> = {};
  const ids = [...new Set(assessments.map((a) => a.unitId))];
  for (const uid of ids) {
    const forUnit = assessments.filter((a) => a.unitId === uid);
    if (forUnit.length < 2) continue;
    const wd = forUnit.map((a) => ({ ...a, dc: decayConf(a.confidence, a.source, a.evidenceDate) }));
    outer: for (let i = 0; i < wd.length; i++) {
      for (let j = i + 1; j < wd.length; j++) {
        const a = wd[i], b = wd[j];
        if (a.source === b.source) continue;
        const diff = Math.abs((CONF_RANK[a.dc] ?? 0) - (CONF_RANK[b.dc] ?? 0));
        if (diff >= 1) {
          const hi = a.sourceTier >= b.sourceTier ? a : b;
          const lo = a.sourceTier >= b.sourceTier ? b : a;
          const overclaim = (CONF_RANK[lo.dc] ?? 0) > (CONF_RANK[hi.dc] ?? 0);
          out[uid] = {
            type: overclaim ? "overclaim" : "underestimate",
            msg: overclaim
              ? `${SOURCE_LABEL[lo.source]} (${lo.dc}) exceeds ${SOURCE_LABEL[hi.source]} (${hi.dc}) — stated experience may not reflect current knowledge`
              : `${SOURCE_LABEL[hi.source]} (${hi.dc}) exceeds ${SOURCE_LABEL[lo.source]} (${lo.dc})`,
          };
          break outer;
        }
      }
    }
  }
  return out;
}

// ─── Small presentational components ─────────────────────────────────────────

function ConfBadge({ value, decayed, original }: { value: ConfLevel; decayed?: boolean; original?: ConfLevel }) {
  const cls: Record<ConfLevel, string> = {
    HIGH: "bg-green-100 text-green-800 border-green-200",
    MEDIUM: "bg-amber-100 text-amber-800 border-amber-200",
    LOW: "bg-red-100 text-red-800 border-red-200",
    UNKNOWN: "bg-muted text-muted-foreground border-border",
  };
  if (decayed && original && original !== value) {
    return (
      <span className="flex items-center gap-1">
        <Badge variant="outline" className={`text-xs opacity-40 line-through ${cls[original]}`}>{original}</Badge>
        <ChevronRight className="h-3 w-3 text-muted-foreground" />
        <Badge variant="outline" className={`text-xs ${cls[value]}`}>{value}</Badge>
        <span className="text-xs text-muted-foreground">(decayed)</span>
      </span>
    );
  }
  return <Badge variant="outline" className={`text-xs ${cls[value]}`}>{value}</Badge>;
}

function TypeBadge({ type }: { type: "application" | "knowledge" }) {
  return (
    <Badge variant="outline" className={`text-xs ${type === "application" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
      {type}
    </Badge>
  );
}

function SrcIcon({ source }: { source: EvidenceSrc }) {
  const icons: Record<EvidenceSrc, React.ReactNode> = {
    resume: <FileText className="h-3.5 w-3.5" />,
    linkedin: <Link2 className="h-3.5 w-3.5" />,
    pretest: <BookOpen className="h-3.5 w-3.5" />,
    scenario: <MessageSquare className="h-3.5 w-3.5" />,
  };
  return <span className="text-muted-foreground" title={SOURCE_LABEL[source]}>{icons[source]}</span>;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  taxonomyLibrary: Taxonomy[];
  onExport?: (profile: LearnerProfile) => void;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function IndividualAssessmentTab({ taxonomyLibrary, onExport }: Props) {
  // Active taxonomy
  const [activeTaxId, setActiveTaxId] = useState<string>(taxonomyLibrary[0]?.customerId ?? "");
  const activeTaxonomy = taxonomyLibrary.find((t) => t.customerId === activeTaxId) ?? taxonomyLibrary[0];
  const units = useMemo(() => (activeTaxonomy ? flattenUnits(activeTaxonomy) : []), [activeTaxonomy]);
  const knowledgeUnits = useMemo(() => units.filter((u) => u.type === "knowledge"), [units]);
  const applicationUnits = useMemo(() => units.filter((u) => u.type === "application"), [units]);

  // Core state
  const [personName, setPersonName] = useState("");
  const [assessments, setAssessments] = useState<Evidence[]>([]);
  const [assessTab, setAssessTab] = useState("documents");

  // Documents tab
  const [docText, setDocText] = useState("");
  const [docSource, setDocSource] = useState<"resume" | "linkedin">("resume");
  const [docWorking, setDocWorking] = useState(false);
  const [docResults, setDocResults] = useState<DocResult[] | null>(null);
  const [docAdded, setDocAdded] = useState(false);
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [linkedinState, setLinkedinState] = useState<"idle" | "trying" | "failed">("idle");
  const [pdfExtracting, setPdfExtracting] = useState(false);
  const [showTextarea, setShowTextarea] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  // Pre-test tab
  const [pretestSelected, setPretestSelected] = useState<string[]>([]);
  const [pretestQuestions, setPretestQuestions] = useState<PreTestQ[]>([]);
  const [pretestGenerating, setPretestGenerating] = useState(false);
  const [pretestCurrentQ, setPretestCurrentQ] = useState(0);
  const [pretestAnswers, setPretestAnswers] = useState<Record<number, number>>({});
  const [pretestScored, setPretestScored] = useState(false);
  const [pretestAdded, setPretestAdded] = useState(false);
  const [pretestError, setPretestError] = useState<string | null>(null);

  // Scenario tab
  const [scenarioUnitId, setScenarioUnitId] = useState(applicationUnits[0]?.id ?? "");
  const [scenarioData, setScenarioData] = useState<ScenarioData | null>(null);
  const [scenarioGenerating, setScenarioGenerating] = useState(false);
  const [scenarioResponse, setScenarioResponse] = useState("");
  const [scenarioEval, setScenarioEval] = useState<ScenarioEval | null>(null);
  const [scenarioEvalWorking, setScenarioEvalWorking] = useState(false);
  const [scenarioAdded, setScenarioAdded] = useState(false);
  const [scenarioError, setScenarioError] = useState<string | null>(null);

  // Report tab
  const applicableRoles = useMemo(() => {
    const unitIds = new Set(units.map((u) => u.id));
    return Object.entries(ROLE_PROFILES).filter(([, ids]) => ids.some((id) => unitIds.has(id)));
  }, [units]);
  const [reportRole, setReportRole] = useState<string>("__all__");

  // Derived
  const coverage = useMemo(() => effectiveCoverage(assessments), [assessments]);
  const conflicts = useMemo(() => detectConflicts(assessments), [assessments]);

  const requiredUnitIds = useMemo(() => {
    if (reportRole === "__all__") return units.map((u) => u.id);
    return ROLE_PROFILES[reportRole] ?? [];
  }, [reportRole, units]);

  function addEvidence(items: Omit<Evidence, "id">[]) {
    setAssessments((prev) => [
      ...prev,
      ...items.map((item) => ({ ...item, id: Math.random().toString(36).slice(2) })),
    ]);
  }

  // ─── Document handlers ───────────────────────────────────────────────────

  async function handleLinkedinFetch() {
    if (!linkedinUrl.trim()) return;
    setLinkedinState("trying");
    try {
      const { data, error } = await invokeFunction("extract-text", { body: { url: linkedinUrl } });
      const text = data?.text as string | undefined;
      if (error || !text || text.length < 400 || /log in|sign in|join now/i.test(text.slice(0, 500))) {
        throw new Error("blocked");
      }
      setDocText(text);
      setDocSource("linkedin");
      setShowTextarea(true);
      setLinkedinState("idle");
    } catch {
      setLinkedinState("failed");
    }
  }

  async function handlePdfUpload(file: File) {
    setPdfExtracting(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((res, rej) => {
        reader.onload = () => res((reader.result as string).split(",")[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const { data, error } = await invokeFunction("extract-text", {
        body: { fileBase64: base64, fileName: file.name, mimeType: file.type },
      });
      if (error || !data?.text) throw new Error((error as { message?: string })?.message ?? "Extraction failed");
      setDocText(data.text as string);
      setDocSource("resume");
      setDocResults(null);
      setDocAdded(false);
      setShowTextarea(false);
    } catch (e) {
      alert(`PDF extraction failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPdfExtracting(false);
    }
  }

  async function handleAssessDocument() {
    if (!docText.trim() || !units.length) return;
    setDocWorking(true);
    setDocResults(null);
    setDocAdded(false);
    try {
      const { data, error } = await invokeFunction("competency-assessment", {
        body: { action: "assess_document", units, docText, docSource },
      });
      if (error) throw new Error((error as { message?: string }).message ?? "Assessment failed");
      setDocResults(data.data as DocResult[]);
    } catch (e) {
      alert(`Assessment failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDocWorking(false);
    }
  }

  function handleAddDocResults() {
    if (!docResults) return;
    addEvidence(
      docResults.map((r) => ({
        unitId: r.unitId,
        confidence: r.confidence,
        evidence: r.evidence,
        evidenceDate: r.evidenceDate,
        source: docSource,
        sourceTier: SOURCE_TIER[docSource],
      }))
    );
    setDocAdded(true);
  }

  // ─── Pre-test handlers ───────────────────────────────────────────────────

  async function handleGeneratePretest() {
    const selected = knowledgeUnits.filter((u) => pretestSelected.includes(u.id));
    if (!selected.length) return;
    setPretestGenerating(true);
    setPretestError(null);
    try {
      const { data, error } = await invokeFunction("competency-assessment", {
        body: { action: "generate_pretest", units: selected },
      });
      if (error) throw new Error((error as { message?: string }).message ?? "Generation failed");
      setPretestQuestions(data.data as PreTestQ[]);
      setPretestCurrentQ(0);
      setPretestAnswers({});
      setPretestScored(false);
      setPretestAdded(false);
    } catch (e) {
      setPretestError(e instanceof Error ? e.message : String(e));
    } finally {
      setPretestGenerating(false);
    }
  }

  function handlePretestAnswer(optionIdx: number) {
    if (pretestAnswers[pretestCurrentQ] !== undefined) return;
    setPretestAnswers((prev) => ({ ...prev, [pretestCurrentQ]: optionIdx }));
  }

  function handlePretestNext() {
    if (pretestCurrentQ < pretestQuestions.length - 1) {
      setPretestCurrentQ((q) => q + 1);
    } else {
      setPretestScored(true);
    }
  }

  function handleAddPretestResults() {
    const today = new Date().toISOString().split("T")[0];
    addEvidence(
      pretestQuestions.map((q, i) => ({
        unitId: q.unitId,
        confidence: pretestAnswers[i] === q.correctIndex ? "HIGH" : "LOW",
        evidence: pretestAnswers[i] === q.correctIndex
          ? "Answered correctly in knowledge pre-test"
          : "Answered incorrectly in knowledge pre-test",
        evidenceDate: today,
        source: "pretest",
        sourceTier: SOURCE_TIER.pretest,
      }))
    );
    setPretestAdded(true);
  }

  function resetPretest() {
    setPretestQuestions([]);
    setPretestSelected([]);
    setPretestCurrentQ(0);
    setPretestAnswers({});
    setPretestScored(false);
    setPretestAdded(false);
    setPretestError(null);
  }

  // ─── Scenario handlers ───────────────────────────────────────────────────

  async function handleGenerateScenario() {
    const unit = units.find((u) => u.id === scenarioUnitId);
    if (!unit) return;
    setScenarioGenerating(true);
    setScenarioData(null);
    setScenarioResponse("");
    setScenarioEval(null);
    setScenarioAdded(false);
    setScenarioError(null);
    try {
      const { data, error } = await invokeFunction("competency-assessment", {
        body: { action: "generate_scenario", unit },
      });
      if (error) throw new Error((error as { message?: string }).message ?? "Generation failed");
      setScenarioData(data.data as ScenarioData);
    } catch (e) {
      setScenarioError(e instanceof Error ? e.message : String(e));
    } finally {
      setScenarioGenerating(false);
    }
  }

  async function handleEvaluateScenario() {
    const unit = units.find((u) => u.id === scenarioUnitId);
    if (!unit || !scenarioData || !scenarioResponse.trim()) return;
    setScenarioEvalWorking(true);
    try {
      const { data, error } = await invokeFunction("competency-assessment", {
        body: { action: "evaluate_scenario", unit, scenarioData, response: scenarioResponse },
      });
      if (error) throw new Error((error as { message?: string }).message ?? "Evaluation failed");
      setScenarioEval(data.data as ScenarioEval);
    } catch (e) {
      alert(`Evaluation failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setScenarioEvalWorking(false);
    }
  }

  function handleAddScenarioResult() {
    if (!scenarioEval) return;
    const today = new Date().toISOString().split("T")[0];
    addEvidence([{
      unitId: scenarioUnitId,
      confidence: scenarioEval.confidence,
      evidence: scenarioEval.verdict,
      evidenceDate: today,
      source: "scenario",
      sourceTier: SOURCE_TIER.scenario,
    }]);
    setScenarioAdded(true);
  }

  function resetScenario() {
    setScenarioData(null);
    setScenarioResponse("");
    setScenarioEval(null);
    setScenarioAdded(false);
    setScenarioError(null);
  }

  // ─── Export ──────────────────────────────────────────────────────────────

  function handleExport() {
    if (!onExport) return;
    const today = new Date().toISOString().split("T")[0];
    const completions: Completion[] = Object.values(coverage).map((cov) => ({
      id: crypto.randomUUID(),
      rawCourseName: `${SOURCE_LABEL[cov.source]} assessment`,
      normalizedCourseName: `${SOURCE_LABEL[cov.source]} — ${cov.unitId}`,
      completionDate: cov.evidenceDate ?? today,
      expiryDate: null,
      issuingBody: "Individual Assessment",
      score: cov.confidence === "HIGH" ? 95 : cov.confidence === "MEDIUM" ? 70 : cov.confidence === "LOW" ? 45 : null,
      status: "active" as const,
      mappedUnits: [{
        unitId: cov.unitId,
        confidence: cov.confidence as "HIGH" | "MEDIUM" | "LOW",
        method: (cov.source === "pretest" || cov.source === "scenario") ? "course_content" as const : "llm_inference" as const,
      }],
    }));
    const profile: LearnerProfile = {
      id: `assessed-${Date.now()}`,
      name: personName || "Assessed individual",
      employeeId: `ASS-${Date.now()}`,
      site: "",
      role: reportRole === "__all__" ? "" : reportRole,
      source: "document" as const,
      importedAt: today,
      completions,
    };
    onExport(profile);
  }

  // ─── Profile panel stats ─────────────────────────────────────────────────

  const totalUnits = units.length;
  const assessedCount = units.filter((u) => coverage[u.id] && coverage[u.id].decayedConfidence !== "UNKNOWN").length;
  const highCount = units.filter((u) => coverage[u.id]?.decayedConfidence === "HIGH").length;
  const medCount = units.filter((u) => coverage[u.id]?.decayedConfidence === "MEDIUM").length;
  const lowCount = units.filter((u) => coverage[u.id]?.decayedConfidence === "LOW").length;
  const conflictCount = Object.keys(conflicts).length;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
      {/* ── Left pane ── */}
      <div className="space-y-4">
        {/* Taxonomy selector */}
        {taxonomyLibrary.length > 1 && (
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium whitespace-nowrap">Taxonomy:</label>
            <select
              value={activeTaxId}
              onChange={(e) => {
                setActiveTaxId(e.target.value);
                setAssessments([]);
                resetPretest();
                resetScenario();
                setDocResults(null);
                setDocAdded(false);
              }}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              {taxonomyLibrary.map((t) => (
                <option key={t.customerId} value={t.customerId}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        <Tabs value={assessTab} onValueChange={setAssessTab}>
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="documents" className="gap-1.5 text-sm"><FileText className="h-3.5 w-3.5" />Documents</TabsTrigger>
            <TabsTrigger value="pretest" className="gap-1.5 text-sm"><BookOpen className="h-3.5 w-3.5" />Pre-test</TabsTrigger>
            <TabsTrigger value="scenario" className="gap-1.5 text-sm"><MessageSquare className="h-3.5 w-3.5" />Scenario</TabsTrigger>
            <TabsTrigger value="report" className="gap-1.5 text-sm"><BarChart2 className="h-3.5 w-3.5" />Assessment report</TabsTrigger>
          </TabsList>

          {/* ── Documents tab ── */}
          <TabsContent value="documents" className="space-y-4 pt-2">
            {/* LinkedIn URL */}
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Link2 className="h-4 w-4" /> LinkedIn profile
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <div className="flex gap-2">
                  <input
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    placeholder="https://linkedin.com/in/your-profile"
                    className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  />
                  <Button size="sm" variant="outline" onClick={handleLinkedinFetch} disabled={!linkedinUrl.trim() || linkedinState === "trying"}>
                    {linkedinState === "trying" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Fetch"}
                  </Button>
                </div>
                {linkedinState === "failed" && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 space-y-1">
                    <p className="font-medium">LinkedIn requires authentication. Two ways to proceed:</p>
                    <ol className="list-decimal pl-4 space-y-0.5">
                      <li>Profile → More → Save to PDF — upload the PDF below</li>
                      <li>Select all text on your LinkedIn profile page, copy, and paste into the text field below</li>
                    </ol>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PDF upload */}
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Upload className="h-4 w-4" /> Resume / PDF
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePdfUpload(f); e.target.value = ""; }}
                />
                <div
                  onClick={() => pdfInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.type === "application/pdf") handlePdfUpload(f); }}
                  className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-4 py-6 cursor-pointer hover:bg-muted/30 transition-colors text-center"
                >
                  {pdfExtracting ? (
                    <>
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Extracting text…</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-muted-foreground" />
                      <p className="text-sm font-medium">Drop a PDF here or click to upload</p>
                      <p className="text-xs text-muted-foreground">Text is extracted locally — nothing is uploaded</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Text paste */}
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Text paste</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDocText(SAMPLE_RESUME); setDocSource("resume"); setShowTextarea(true); }}>
                      Load sample resume
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowTextarea((v) => !v)}>
                      {showTextarea ? "Hide" : "Paste text"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {(showTextarea || docText) && (
                <CardContent className="px-4 pb-4">
                  <textarea
                    value={docText}
                    onChange={(e) => setDocText(e.target.value)}
                    rows={8}
                    placeholder="Paste resume or LinkedIn profile text here…"
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                  />
                </CardContent>
              )}
            </Card>

            {/* Assess button */}
            <div className="flex items-center gap-3">
              <select
                value={docSource}
                onChange={(e) => setDocSource(e.target.value as "resume" | "linkedin")}
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="resume">Resume</option>
                <option value="linkedin">LinkedIn</option>
              </select>
              <Button
                disabled={!docText.trim() || docWorking || !units.length}
                onClick={handleAssessDocument}
                className="gap-2"
              >
                {docWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Assess with AI
              </Button>
            </div>

            {/* Results */}
            {docResults && (
              <Card>
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Assessment results</CardTitle>
                    {docAdded ? (
                      <span className="flex items-center gap-1 text-xs text-green-600"><Check className="h-3.5 w-3.5" /> Added to profile</span>
                    ) : (
                      <Button size="sm" onClick={handleAddDocResults}>Add to profile</Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {docResults.map((r) => {
                    const dc = decayConf(r.confidence, docSource, r.evidenceDate);
                    const isDecayed = dc !== r.confidence;
                    const unit = units.find((u) => u.id === r.unitId);
                    return (
                      <div key={r.unitId} className="flex items-start gap-3 px-4 py-3 border-t first:border-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-mono text-xs text-muted-foreground">{r.unitId}</span>
                            <span className="text-sm">{unit?.label ?? r.unitId}</span>
                            {unit && <TypeBadge type={unit.type} />}
                          </div>
                          {r.confidence !== "UNKNOWN" && (
                            <>
                              {r.evidence && <p className="text-xs text-muted-foreground italic mb-0.5">"{r.evidence}"</p>}
                              {r.evidenceDate && (
                                <p className="text-xs text-muted-foreground">
                                  {new Date(r.evidenceDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                                </p>
                              )}
                              {r.reasoning && <p className="text-xs text-muted-foreground/70 mt-0.5">{r.reasoning}</p>}
                            </>
                          )}
                        </div>
                        <ConfBadge value={dc} decayed={isDecayed} original={r.confidence} />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Pre-test tab ── */}
          <TabsContent value="pretest" className="space-y-4 pt-2">
            {!pretestQuestions.length && !pretestGenerating && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Select knowledge-type units to test:</p>
                {knowledgeUnits.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No knowledge-type units in this taxonomy.</p>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <Button size="sm" variant="outline" onClick={() => setPretestSelected(knowledgeUnits.map((u) => u.id))}>Select all</Button>
                      <Button size="sm" variant="outline" onClick={() => setPretestSelected([])}>Clear</Button>
                    </div>
                    <div className="space-y-1.5">
                      {knowledgeUnits.map((u) => (
                        <label key={u.id} className="flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer hover:bg-muted/30">
                          <input
                            type="checkbox"
                            checked={pretestSelected.includes(u.id)}
                            onChange={(e) => setPretestSelected((prev) => e.target.checked ? [...prev, u.id] : prev.filter((id) => id !== u.id))}
                            className="h-4 w-4 accent-primary"
                          />
                          <span className="font-mono text-xs text-muted-foreground w-14 shrink-0">{u.id}</span>
                          <span className="text-sm">{u.label}</span>
                        </label>
                      ))}
                    </div>
                    {pretestError && <p className="text-sm text-destructive">{pretestError}</p>}
                    <Button disabled={!pretestSelected.length} onClick={handleGeneratePretest} className="gap-2">
                      <Sparkles className="h-4 w-4" />
                      Generate questions ({pretestSelected.length})
                    </Button>
                  </>
                )}
              </div>
            )}

            {pretestGenerating && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Generating questions…</p>
              </div>
            )}

            {pretestQuestions.length > 0 && !pretestScored && (
              <div className="space-y-4">
                {/* Progress dots */}
                <div className="flex gap-1.5 justify-center">
                  {pretestQuestions.map((_, i) => (
                    <div key={i} className={`w-2.5 h-2.5 rounded-full ${
                      i < pretestCurrentQ
                        ? pretestAnswers[i] === pretestQuestions[i].correctIndex ? "bg-green-500" : "bg-red-500"
                        : i === pretestCurrentQ ? "bg-foreground" : "border border-border bg-background"
                    }`} />
                  ))}
                </div>
                {/* Question card */}
                <Card>
                  <CardHeader className="pb-2">
                    <p className="text-xs text-muted-foreground">Question {pretestCurrentQ + 1} of {pretestQuestions.length}</p>
                    <CardTitle className="text-base font-medium leading-snug">
                      {pretestQuestions[pretestCurrentQ].question}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {pretestQuestions[pretestCurrentQ].options.map((opt, i) => {
                      const answered = pretestAnswers[pretestCurrentQ] !== undefined;
                      const correct = i === pretestQuestions[pretestCurrentQ].correctIndex;
                      const selected = pretestAnswers[pretestCurrentQ] === i;
                      return (
                        <button
                          key={i}
                          onClick={() => handlePretestAnswer(i)}
                          disabled={answered}
                          className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                            answered
                              ? correct ? "border-green-500 bg-green-50 text-green-800"
                                : selected ? "border-red-500 bg-red-50 text-red-800"
                                : "border-border bg-muted/20 text-muted-foreground"
                              : "border-border hover:bg-muted/40 cursor-pointer"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                    {pretestAnswers[pretestCurrentQ] !== undefined && (
                      <>
                        <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground mt-2">
                          {pretestQuestions[pretestCurrentQ].explanation}
                        </div>
                        <div className="flex justify-end pt-1">
                          <Button size="sm" onClick={handlePretestNext} className="gap-1.5">
                            {pretestCurrentQ < pretestQuestions.length - 1 ? <>Next <ChevronRight className="h-3.5 w-3.5" /></> : "See results"}
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {pretestScored && (
              <div className="space-y-4">
                {/* Score */}
                {(() => {
                  const correct = pretestQuestions.filter((q, i) => pretestAnswers[i] === q.correctIndex).length;
                  const pct = Math.round((correct / pretestQuestions.length) * 100);
                  return (
                    <Card>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">Score</span>
                          <span className="font-semibold">{correct}/{pretestQuestions.length} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
                {/* Per-unit results */}
                <div className="space-y-1">
                  {pretestQuestions.map((q, i) => {
                    const correct = pretestAnswers[i] === q.correctIndex;
                    const unit = units.find((u) => u.id === q.unitId);
                    return (
                      <div key={i} className="flex items-center gap-3 rounded-md border px-3 py-2.5 text-sm">
                        {correct ? <Check className="h-4 w-4 text-green-500 shrink-0" /> : <span className="h-4 w-4 text-red-500 shrink-0 font-bold text-center">✗</span>}
                        <span className="font-mono text-xs text-muted-foreground w-14 shrink-0">{q.unitId}</span>
                        <span className="flex-1 truncate">{unit?.label ?? q.unitId}</span>
                        <ConfBadge value={correct ? "HIGH" : "LOW"} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3">
                  {pretestAdded ? (
                    <span className="flex items-center gap-1 text-sm text-green-600"><Check className="h-4 w-4" /> Added to profile</span>
                  ) : (
                    <Button onClick={handleAddPretestResults}>Add to profile</Button>
                  )}
                  <Button variant="outline" className="gap-1.5" onClick={resetPretest}><RotateCcw className="h-3.5 w-3.5" /> Start over</Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Scenario tab ── */}
          <TabsContent value="scenario" className="space-y-4 pt-2">
            {applicationUnits.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No application-type units in this taxonomy.</p>
            ) : (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="text-sm font-medium whitespace-nowrap">Unit:</label>
                  <select
                    value={scenarioUnitId}
                    onChange={(e) => { setScenarioUnitId(e.target.value); resetScenario(); }}
                    className="h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  >
                    {applicationUnits.map((u) => (
                      <option key={u.id} value={u.id}>{u.id} — {u.label}</option>
                    ))}
                  </select>
                  {!scenarioData && !scenarioGenerating && (
                    <Button onClick={handleGenerateScenario} className="gap-2 shrink-0">
                      <Sparkles className="h-4 w-4" /> Generate scenario
                    </Button>
                  )}
                </div>

                {scenarioGenerating && (
                  <div className="flex items-center justify-center py-12 gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Generating scenario…</p>
                  </div>
                )}

                {scenarioError && <p className="text-sm text-destructive">{scenarioError}</p>}

                {scenarioData && (
                  <div className="space-y-4">
                    <Card>
                      <CardContent className="p-4 space-y-3">
                        <p className="text-sm leading-relaxed">{scenarioData.scenario}</p>
                        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2.5">
                          <p className="text-xs font-medium text-blue-800 mb-0.5">Your task</p>
                          <p className="text-sm text-blue-900">{scenarioData.prompt}</p>
                        </div>
                      </CardContent>
                    </Card>

                    {!scenarioEval && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium">Your response</label>
                          <span className="text-xs text-muted-foreground">
                            {scenarioResponse.trim().split(/\s+/).filter(Boolean).length} words
                          </span>
                        </div>
                        <textarea
                          value={scenarioResponse}
                          onChange={(e) => setScenarioResponse(e.target.value)}
                          rows={8}
                          placeholder="Write your response here (200–350 words)…"
                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                        />
                        <Button
                          disabled={!scenarioResponse.trim() || scenarioEvalWorking}
                          onClick={handleEvaluateScenario}
                          className="gap-2"
                        >
                          {scenarioEvalWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                          Evaluate
                        </Button>
                      </div>
                    )}

                    {scenarioEval && (
                      <Card>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Score: {scenarioEval.score}/100</span>
                            <ConfBadge value={scenarioEval.confidence} />
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${scenarioEval.score >= 80 ? "bg-green-500" : scenarioEval.score >= 60 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${scenarioEval.score}%` }} />
                          </div>
                          <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800">
                            <span className="font-medium">Strengths: </span>{scenarioEval.strengths}
                          </div>
                          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                            <span className="font-medium">Gaps: </span>{scenarioEval.gaps}
                          </div>
                          <p className="text-xs text-muted-foreground italic">{scenarioEval.verdict}</p>
                          <div className="flex gap-3 pt-1">
                            {scenarioAdded ? (
                              <span className="flex items-center gap-1 text-sm text-green-600"><Check className="h-4 w-4" /> Added to profile</span>
                            ) : (
                              <Button size="sm" onClick={handleAddScenarioResult}>Add to profile</Button>
                            )}
                            <Button size="sm" variant="outline" className="gap-1.5" onClick={resetScenario}><RotateCcw className="h-3.5 w-3.5" /> Try another</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ── Report tab ── */}
          <TabsContent value="report" className="space-y-4 pt-2">
            {assessments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Complete at least one assessment to generate a report.</p>
              </div>
            ) : (
              <>
                {/* Role selector */}
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="text-sm font-medium whitespace-nowrap">Role profile:</label>
                  <select
                    value={reportRole}
                    onChange={(e) => setReportRole(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  >
                    <option value="__all__">All units</option>
                    {applicableRoles.map(([role]) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                  {onExport && (
                    <Button size="sm" variant="outline" className="gap-1.5 ml-auto" onClick={handleExport}>
                      <Download className="h-3.5 w-3.5" /> Export to Learner Hub
                    </Button>
                  )}
                </div>

                {/* Readiness card */}
                {(() => {
                  const ready = requiredUnitIds.filter((id) => (CONF_RANK[coverage[id]?.decayedConfidence ?? "UNKNOWN"] ?? 0) >= 2).length;
                  const partial = requiredUnitIds.filter((id) => coverage[id]?.decayedConfidence === "LOW").length;
                  const gaps = requiredUnitIds.filter((id) => !coverage[id] || coverage[id].decayedConfidence === "UNKNOWN").length;
                  const pct = requiredUnitIds.length > 0 ? Math.round((ready / requiredUnitIds.length) * 100) : 0;
                  const label = pct >= 80 ? "Ready" : pct >= 50 ? "Partially ready" : "Needs development";
                  const barColor = pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
                  return (
                    <Card>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-baseline justify-between">
                          <div>
                            <p className="text-sm font-medium">{reportRole === "__all__" ? (personName || "Individual") : reportRole}</p>
                            <p className="text-xs text-muted-foreground">{label}</p>
                          </div>
                          <span className="text-3xl font-bold">{pct}%</span>
                        </div>
                        <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-center text-xs">
                          <div className="rounded-md bg-green-50 border border-green-200 py-2">
                            <p className="text-lg font-bold text-green-700">{ready}</p>
                            <p className="text-green-600">Confirmed</p>
                          </div>
                          <div className="rounded-md bg-amber-50 border border-amber-200 py-2">
                            <p className="text-lg font-bold text-amber-700">{partial}</p>
                            <p className="text-amber-600">Partial</p>
                          </div>
                          <div className="rounded-md bg-muted py-2">
                            <p className="text-lg font-bold">{gaps}</p>
                            <p className="text-muted-foreground">Gaps</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Assessment table */}
                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b bg-muted/40 text-xs">
                          <th className="text-left px-3 py-2.5 font-medium min-w-[180px]">Unit</th>
                          <th className="text-left px-3 py-2.5 font-medium min-w-[80px]">Sources</th>
                          <th className="text-left px-3 py-2.5 font-medium min-w-[120px]">Confidence</th>
                          <th className="text-left px-3 py-2.5 font-medium min-w-[80px]">Status</th>
                          <th className="text-left px-3 py-2.5 font-medium min-w-[160px]">Evidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requiredUnitIds.map((uid) => {
                          const unit = units.find((u) => u.id === uid);
                          const best = coverage[uid];
                          const conflict = conflicts[uid];
                          const allEv = assessments.filter((a) => a.unitId === uid);
                          const dc: ConfLevel = best?.decayedConfidence ?? "UNKNOWN";
                          const isGap = !best || dc === "UNKNOWN";
                          const rowBg = conflict ? "bg-amber-50/60" : isGap ? "bg-muted/20" : dc === "LOW" ? "bg-red-50/40" : dc === "MEDIUM" ? "bg-amber-50/30" : "";
                          const uniqueSources = [...new Map(allEv.map((e) => [e.source, e])).values()];
                          return (
                            <tr key={uid} className={`border-b last:border-0 ${rowBg}`}>
                              <td className="px-3 py-2.5">
                                <div className="flex flex-col gap-0.5">
                                  <span className="font-mono text-xs text-muted-foreground">{uid}</span>
                                  <span className="text-sm">{unit?.label ?? uid}</span>
                                  {unit && <TypeBadge type={unit.type} />}
                                </div>
                              </td>
                              <td className="px-3 py-2.5">
                                {uniqueSources.length > 0 ? (
                                  <div className="flex flex-col gap-1">
                                    {uniqueSources.map((e) => (
                                      <div key={e.source} className="flex items-center gap-1">
                                        <SrcIcon source={e.source} />
                                        {e.evidenceDate && (
                                          <span className="text-xs text-muted-foreground">
                                            {new Date(e.evidenceDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5">
                                {best
                                  ? <ConfBadge value={dc} decayed={dc !== best.confidence} original={best.confidence} />
                                  : <ConfBadge value="UNKNOWN" />}
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex flex-col gap-1">
                                  {isGap && <Badge variant="outline" className="text-xs bg-muted text-muted-foreground w-fit">Not evidenced</Badge>}
                                  {conflict && (
                                    <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300 w-fit gap-1">
                                      <AlertTriangle className="h-3 w-3" /> Conflict
                                    </Badge>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2.5">
                                {best?.evidence && (
                                  <p className="text-xs text-muted-foreground line-clamp-2">{best.evidence}</p>
                                )}
                                {conflict && (
                                  <p className="text-xs text-amber-700 mt-0.5">{conflict.msg}</p>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Right pane — Profile panel ── */}
      <div className="lg:sticky lg:top-4 space-y-4">
        <Card>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Competency profile</p>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            {/* Name */}
            <input
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              placeholder="Candidate name"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            />

            {/* Coverage summary */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Coverage (decay-adjusted)</span>
                <span className="font-medium">{assessedCount}/{totalUnits}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: totalUnits > 0 ? `${(assessedCount / totalUnits) * 100}%` : "0%" }} />
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-center">
                {([["HIGH", highCount, "text-green-700 bg-green-50 border-green-200"], ["MED", medCount, "text-amber-700 bg-amber-50 border-amber-200"], ["LOW", lowCount, "text-red-700 bg-red-50 border-red-200"]] as const).map(([label, count, cls]) => (
                  <div key={label} className={`rounded border py-1.5 text-xs font-medium ${cls}`}>
                    <p className="text-base font-bold">{count}</p>{label}
                  </div>
                ))}
              </div>
            </div>

            {/* Conflict alert */}
            {conflictCount > 0 && (
              <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {conflictCount} unit{conflictCount !== 1 ? "s" : ""} with conflicting evidence
              </div>
            )}

            {/* Unit list */}
            {units.length > 0 && (
              <ScrollArea className="h-[280px]">
                <div className="space-y-0.5 pr-2">
                  {units.map((u) => {
                    const best = coverage[u.id];
                    const dc = best?.decayedConfidence ?? "UNKNOWN";
                    const hasConflict = !!conflicts[u.id];
                    const srcSet = [...new Set(assessments.filter((a) => a.unitId === u.id).map((a) => a.source))];
                    const confCls: Record<ConfLevel, string> = {
                      HIGH: "bg-green-100 text-green-800 border-green-200",
                      MEDIUM: "bg-amber-100 text-amber-800 border-amber-200",
                      LOW: "bg-red-100 text-red-800 border-red-200",
                      UNKNOWN: "bg-muted text-muted-foreground border-border",
                    };
                    return (
                      <div key={u.id} className="flex items-center gap-1.5 py-1.5 text-xs">
                        <span className="font-mono font-semibold text-muted-foreground w-14 shrink-0">{u.id}</span>
                        <span className="flex-1 truncate text-muted-foreground">{u.label}</span>
                        <div className="flex items-center gap-0.5">
                          {srcSet.map((s) => <SrcIcon key={s} source={s} />)}
                          {hasConflict && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                        </div>
                        <Badge variant="outline" className={`text-[10px] px-1 py-0 h-4 shrink-0 ${confCls[dc]}`}>{dc === "UNKNOWN" ? "—" : dc}</Badge>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            {/* Source legend */}
            <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground pt-1 border-t">
              {(["resume", "linkedin", "pretest", "scenario"] as EvidenceSrc[]).map((s) => (
                <span key={s} className="flex items-center gap-1"><SrcIcon source={s} />{SOURCE_LABEL[s]}</span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground/70">Confidence is decay-adjusted. Strikethrough = original before decay.</p>

            {/* Clear */}
            {assessments.length > 0 && (
              <Button variant="outline" size="sm" className="w-full text-destructive hover:text-destructive" onClick={() => { setAssessments([]); setDocResults(null); setDocAdded(false); }}>
                Clear profile
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
